import fetchLoyaltyPurchasePointsOffers, {
  getLoyaltyPurchasePointsOffersKey,
} from 'actions/loyalty/fetchLoyaltyPurchasePointsOffers'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getCurrentUserId, selectLoggedIn } from 'selectors/accountSelectors'
import { useEffect, useMemo } from 'react'

interface ReturnType {
  offers: Array<App.LoyaltyPurchasePointsOffer>
  fetching: boolean
  error: any
  /** True iff the active fetch is the per-customer (eligibility-aware) variant. */
  hasCustomerEligibility: boolean
  /**
   * Whether the member has at least one purchasable offer. Defaults to `true`
   * while loading or for unauthenticated visitors so buy-points entry points
   * don't flash hide-then-show before eligibility is known. Returns `false`
   * on fetch error — no positive eligibility, no claim of eligibility.
   */
  customerHasEligibleOffers: boolean
}

/**
 * Fetches Société points-purchase offers for the current currency.
 *
 * - Logged-out: hits the public `/points/purchase-offers` endpoint (no
 *   eligibility data; offers are pricing-only).
 * - Logged-in: hits `/customer/:customerId/points/purchase-offers` for
 *   per-customer eligibility (`isEligibleNow`, `nextEligiblePurchaseDate`).
 *
 * Cached by `currency` (logged-out) or `${currency}:${customerId}` (logged-in).
 */
function useLoyaltyPurchasePointsOffers(): ReturnType {
  const dispatch = useAppDispatch()
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const customerId = useAppSelector(getCurrentUserId)
  const effectiveCustomerId = isLoggedIn ? customerId : undefined
  const key = getLoyaltyPurchasePointsOffersKey(currency, effectiveCustomerId)

  const cached = useAppSelector(state => state.loyalty.purchaseOffers[key])

  useEffect(() => {
    if (currency) {
      dispatch(fetchLoyaltyPurchasePointsOffers(currency, effectiveCustomerId))
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currency, effectiveCustomerId])

  const offers = cached?.data ?? EmptyArray
  const fetching = !!cached?.fetching || (!cached?.data && !cached?.error)
  const error = cached?.error
  /** Customer attached to request */
  const hasCustomerEligibility = !!effectiveCustomerId
  const customerHasEligibleOffers = useMemo(() => {
    if (error) return false
    if (!isLoggedIn) return true
    if (fetching) return true
    return offers.some(offer => offer.isEligibleNow !== false)
  }, [error, isLoggedIn, fetching, offers])

  return {
    offers,
    fetching,
    error,
    hasCustomerEligibility,
    customerHasEligibleOffers,
  }
}

export default useLoyaltyPurchasePointsOffers
